import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {SearchDiagnosticsService} from "../../service/SearchDiagnosticsService";
import {Loading} from "../../utils/InlineComponents";

const service = new SearchDiagnosticsService();

// An outcome is a closed set on the server, so the colour map is exhaustive by
// construction: anything unmapped would be a new server-side outcome and shows neutral.
const OUTCOME_SEVERITY = {
    suggested: 'success',
    already_competitor: 'info',
    duplicate: 'info',
    excluded_host: 'warning',
    filtered_by_seller_filter: 'warning',
    // Actionable, not policy: no spider claims the host, so writing one is the fix.
    // Ranked with the other broken outcomes rather than with the exclusions.
    no_spider_for_host: 'danger',
    ignored_by_engine_rule: 'warning',
    suggested_but_dropped: 'warning',
    fetch_failed: 'danger',
    spider_error: 'danger',
    no_parser: 'danger',
};

// A reader opens this screen to find what is worth acting on, so the results table leads
// with it: everything broken first, then the deliberate exclusions, then the rows that
// worked. The order is derived from OUTCOME_SEVERITY rather than listing the outcomes a
// second time, so the sort can never drift from the badge colours a reader sees.
const OUTCOME_ORDER = {danger: 0, warning: 1, info: 2, success: 3};
const actionability = (row) => {
    const rank = OUTCOME_ORDER[OUTCOME_SEVERITY[row.outcome]];
    return rank === undefined ? OUTCOME_ORDER.info : rank;
};

const ProductSearchDiagnostics = ({selectedProduct}) => {

    const {t, setError} = React.useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    // Which outcome the results table is narrowed to, or null for all of them. Clicking a
    // count is how a reader gets from "3 could not be fetched" to the three rows.
    const [outcomeFilter, setOutcomeFilter] = useState(null);

    useEffect(() => {
        if (!selectedProduct?.id) return;
        setLoading(true);
        service.getProductSearchDiagnostics(selectedProduct.id)
            .then(result => { setData(result); setLoading(false); })
            .catch(error => { setError(error); setError(null); setLoading(false); });
    }, [selectedProduct?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <Loading/>;
    if (!data) return <Message severity="info" text={t('search_diagnostics.never_searched')}/>;

    const run = data.run;
    const queries = data.queries || [];
    const results = data.results || [];
    const label = (key) => t(`search_diagnostics.${key}`);
    const emptyMessage = t('actions.empty_message');

    const outcomeBody = (row) => <Tag severity={OUTCOME_SEVERITY[row.outcome] || null}
                                      value={t(`search_diagnostics.outcome_${row.outcome}`)}/>;

    // Array.prototype.sort is stable, so rows keep their original page/position order
    // within each band - the actionable ones simply rise above the rest.
    const orderedResults = [...results].sort((a, b) => actionability(a) - actionability(b));
    const shownResults = outcomeFilter
        ? orderedResults.filter(row => row.outcome === outcomeFilter)
        : orderedResults;

    // A count is a filter: clicking narrows the table to those rows, clicking the active
    // one (or the total) clears it again.
    const countTag = (outcome, severity, text) => {
        const active = outcomeFilter === outcome;
        return <span key={outcome || 'all'} onClick={() => setOutcomeFilter(active ? null : outcome)}
                     title={label('filter_by_outcome')}
                     style={{cursor: 'pointer', marginRight: '.5em', marginBottom: '.35em',
                             display: 'inline-block',
                             opacity: outcomeFilter && !active ? 0.45 : 1}}>
            <Tag severity={severity} value={text}
                 style={active ? {boxShadow: '0 0 0 2px var(--text-color)'} : null}/>
        </span>;
    };

    // Group pages by `query_index` (the position in `queries` that produced them), NOT by
    // query text: the chunked seller-filter path records the SAME query text once per host
    // chunk, differing only in restrict_hosts, so matching on text would collapse those
    // chunks into one group with page numbers running 1,2,3,1,2,3 - reading as a paging bug
    // that does not exist. `query_index` is null for shopping entries, which record no
    // query at all; those get their own clearly-labelled group rather than being folded
    // into the first indexed group or silently dropped.
    const queryLabel = (entry) => entry.restrict_hosts && entry.restrict_hosts.length
        ? `${label('restrict_hosts')}: ${entry.restrict_hosts.join(', ')}`
        : label('all_hosts');
    const allPages = data.pages || [];
    const pagesByQuery = queries.map((entry, index) => ({
        entry,
        index,
        pages: allPages.filter(page => page.query_index === index),
    }));
    const noQueryPages = allPages.filter(page => page.query_index == null);
    // Defensive only: a query_index that does not resolve to an entry in `queries` should
    // not happen, but a page row must never silently vanish from the view if it does.
    const orphanPages = allPages.filter(page => page.query_index != null &&
        !(page.query_index >= 0 && page.query_index < queries.length));

    const droppedByReason = (data.counts && data.counts.dropped_by_reason) || {};

    const pagesTable = (pages, key) => <DataTable value={pages} responsive={true}
            className="p-datatable-sm" emptyMessage={emptyMessage} key={key}>
        <Column field="page" header={label('page')} style={{width: '5rem'}}/>
        <Column field="http_status" header={label('http')} style={{width: '6rem'}}/>
        <Column field="result_count" header={label('results')}/>
        <Column header={label('coverage')}
                body={(row) => row.coverage == null ? label('not_scored') : row.coverage.toFixed(2)}/>
        <Column header={label('rejected')} body={(row) => row.rejected ? '✓' : ''}
                style={{width: '5rem'}}/>
        <Column field="attempts" header={label('attempts')}/>
        <Column header={label('engine_total')}
                body={(row) => row.engine_total_results == null
                    ? '-' : row.engine_total_results.toLocaleString()}/>
        {/* The exact SERP we asked for, openable so a reader can see what the engine
            actually answers today. The scraper records the URL we requested, never the
            one a proxy rewrote it into - that one carries our API key. */}
        <Column header={label('engine_url')} style={{width: '5rem'}}
                body={(row) => row.url
                    ? <a href={row.url} target="_blank" rel="noreferrer">{label('open')}</a>
                    : '-'}/>
    </DataTable>;

    return <div>
        <div style={{marginBottom: '.75em', color: 'var(--text-color-secondary)'}}>
            {data.searched_at && new Date(data.searched_at).toLocaleString()}
            {run && <> · {run.engine} · {run.proxy_provider} · {run.trigger}
                {run.settings?.page_number && <> · {run.settings.page_number} {label('pages')}</>}</>}
            {!run && <> · {label('run_expired')}</>}
        </div>

        <h6>{label('queries')}</h6>
        {queries.length === 0 && <Message severity="info" text={emptyMessage}/>}
        {queries.map((entry, index) => <div key={index} style={{marginBottom: '.5em'}}>
            <code style={{userSelect: 'all'}}>{entry.query}</code>
            {' — '}
            <span style={{color: 'var(--text-color-secondary)'}}>{queryLabel(entry)}</span>
        </div>)}

        {data.counts && <div style={{margin: '1em 0'}}>
            {countTag(null, null, `${label('returned')}: ${data.counts.returned}`)}
            {countTag('suggested', 'success', `${label('suggested')}: ${data.counts.suggested}`)}
            {Object.entries(droppedByReason).map(([reason, count]) => countTag(
                reason, OUTCOME_SEVERITY[reason] || 'warning',
                `${t(`search_diagnostics.outcome_${reason}`)}: ${count}`))}
        </div>}

        <h6 style={{marginTop: '1.5em'}}>{label('pages')} <span style={{fontWeight: 'normal',
             fontSize: '.8em', color: 'var(--text-color-secondary)'}}>— {label('engine_total_hint')}</span></h6>
        {pagesByQuery.map(({entry, index, pages}) => pages.length > 0 && <div key={index}
                style={{marginBottom: '1em'}}>
            <div style={{color: 'var(--text-color-secondary)', marginBottom: '.25em'}}>
                <code>{entry.query}</code>{' — '}{queryLabel(entry)}
            </div>
            {pagesTable(pages, index)}
        </div>)}
        {noQueryPages.length > 0 && <div style={{marginBottom: '1em'}}>
            <div style={{color: 'var(--text-color-secondary)', marginBottom: '.25em'}}>
                {label('no_query_pages')}
            </div>
            {pagesTable(noQueryPages, 'no-query')}
        </div>}
        {orphanPages.length > 0 && <div style={{marginBottom: '1em'}}>
            <div style={{color: 'var(--text-color-secondary)', marginBottom: '.25em'}}>
                {label('unmatched_pages')}
            </div>
            {pagesTable(orphanPages, 'orphan')}
        </div>}

        <h6 style={{marginTop: '1.5em'}}>{label('results')}
            <span style={{fontWeight: 'normal', marginLeft: '.5em',
                 color: 'var(--text-color-secondary)'}}>
                {outcomeFilter
                    ? <>{shownResults.length} · {t(`search_diagnostics.outcome_${outcomeFilter}`)}</>
                    : data.counts && <>{data.counts.returned} {label('returned')} · {data.counts.suggested} {label('suggested')}</>}
            </span></h6>
        <DataTable value={shownResults} responsive={true} className="p-datatable-sm"
                   emptyMessage={emptyMessage}>
            <Column field="position" header={label('position')} style={{width: '4rem'}}/>
            <Column field="page" header={label('page')} style={{width: '5rem'}}/>
            <Column field="host" header={label('host')}/>
            <Column header="URL" body={(row) => <a href={row.url} target="_blank"
                                                   rel="noreferrer">{row.url}</a>}/>
            <Column header={label('outcome')} body={outcomeBody} sortable={true} field="outcome"/>
            <Column header={label('reason')}
                    body={(row) => [row.reason, row.detail].filter(Boolean).join(' — ')}/>
        </DataTable>
    </div>;
};

export default ProductSearchDiagnostics;
